import { useMutation, useQuery } from "@apollo/client";
import { useFormik } from "formik";
import { useEffect } from "react";
import * as Yup from "yup";

import { GridInputs } from "../../../../../../../components/Functional";
import { Button } from "../../../../../../../components/ui";
import {
  CREATE_CONDITION,
  UPDATE_CONDITION,
} from "../../../../../../../graphql/mutation/Condition";
import { FETCH_CONDITION_STATUS } from "../../../../../../../graphql/query/ConditionStatus";
import { filterEmptyValues } from "../../../../../../../util";

const AddCondition = (props = {}) => {
  const { closeModal, instance, isOpen, row, user } = props;
  const {
    data: { conditionStatus = [], loading: conditionStatusLoading } = {},
  } = useQuery(FETCH_CONDITION_STATUS);
  const [createCondition, createOptions] = useMutation(CREATE_CONDITION(), {
    refetchQueries: ["FetchConditionsV2"],
  });
  const [updateCondition, updateOptions] = useMutation(UPDATE_CONDITION(), {
    refetchQueries: ["FetchConditionsV2"],
  });
  const loading = createOptions.loading || updateOptions.loading;
  const error = createOptions.error || updateOptions.error;
  const reset = () => {
    createOptions.reset();
    updateOptions.reset();
  };
  const isEditing = !!row?.id; // if modal is opened from list, row will be a condition
  const USER_ACTION = isEditing ? "Edit" : "Add";

  const formik = useFormik({
    validationSchema: Yup.object({
      description: Yup.string().trim().required("Required"),
      statusId: Yup.string().required("Required").typeError("Required"),
      mountStatusId: Yup.string().notRequired(),
    }),
    initialValues: {
      createdAt: row?.date ?? "",
      updatedAt: row?.time?.split?.("T")?.[0] ?? "",
      user: row?.user ?? user?.name,
      statusId: row?.status?.id ?? "",
      mountStatusId: row?.mountStatus?.id ?? "",
      description: isEditing ? row?.description : "Summary: ",
    },
    enableReinitialize: true,
  });

  useEffect(() => {
    formik.resetForm();
    formik.validateForm();
    reset();
  }, [isOpen]);

  const handleEdit = async () => {
    const input = {
      id: row.id,
      ...filterEmptyValues(formik.values, ["createdAt", "updatedAt", "user"]),
    };

    await updateCondition({
      variables: { input },
      update: (cache, { data }) => {
        const { updateCondition: { success } = {} } = data;
        if (success) {
          cache.evict({
            id: cache.identify({
              __typename: "Condition",
              id: row.id,
            }),
          });
        }
      },
    });

    closeModal();
  };

  const handleCreate = async () => {
    const input = {
      instanceId: instance?.id,
      ...filterEmptyValues(formik.values, ["createdAt", "updatedAt", "user"]),
    };

    await createCondition({ variables: { input } });

    closeModal();
  };

  const inputs = {
    className: "grid grid-rows-1 gap-4",
    inputs: [
      {
        className:
          "grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3",
        inputs: [
          {
            label: "User",
            name: "user",
            type: "text",
            disabled: true,
          },
          {
            label: "Created",
            name: "createdAt",
            type: "text",
            disabled: true,
          },
          {
            label: "Updated",
            name: "updatedAt",
            type: "text",
            disabled: true,
          },
        ],
      },
      {
        className: "grid grid-rows-1",
        label: "Description",
        name: "description",
        type: "textarea",
        rows: 5,
      },
      {
        className: "grid grid-cols-1 gap-4 sm:grid-cols-2",
        inputs: [
          {
            label: "Status",
            name: "statusId",
            type: "multi-select",
            options: conditionStatus?.map(status => ({
              label: status?.description,
              value: status?.id,
            })),
          },
          {
            label: "Frame Status",
            name: "mountStatusId",
            type: "multi-select",
            options: conditionStatus?.map(status => ({
              label: status?.description,
              value: status?.id,
            })),
          },
        ],
      },
    ],
  };

  const inputProps = {
    formik,
    ...inputs,
  };

  if (conditionStatusLoading) return null;

  return (
    <div className="flex w-full flex-col p-4 sm:p-6 lg:p-8">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
        <div className="flex flex-1 flex-col text-xl font-bold">
          {USER_ACTION} Condition for {instance?.name}
        </div>
        <div className="flex flex-wrap gap-2 sm:gap-4">
          <Button
            action="default"
            label="Cancel"
            onClick={() => {
              closeModal();
            }}
          />
          <Button
            label={`${USER_ACTION}${loading ? "ing" : ""}`}
            disabled={!formik.dirty || loading || !formik.isValid}
            onClick={isEditing ? handleEdit : handleCreate}
          />
        </div>
      </div>
      <div className="grid w-full grid-cols-1 pb-10 pt-20">
        {error && <div className="font-bold text-red-700">{error.message}</div>}
        <div className="flex flex-col gap-4 sm:gap-6 lg:gap-8">
          <GridInputs {...inputProps} />
        </div>
      </div>
    </div>
  );
};

export default AddCondition;
